import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getFirestore, doc, getDoc, updateDoc, serverTimestamp,
  collection, getDocs, deleteDoc, setDoc
} from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PaymentMethods from '../components/PaymentMethods';
import { MAPBOX_TOKEN } from '../firebaseConfig';
import Swal from 'sweetalert2';
import './AccountPage.css';

const db = getFirestore();
const functions = getFunctions();

  const AccountPage = () => {
  const navigate = useNavigate();
  const { user, auth, logout, hasLocation, reloadAddresses, loading: authLoading, favorites, addRemoveFavorite, reloadFavorites } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/logout');
  };
  const [profile, setProfile] = useState(null);
  // Every account (consumer or business) lives at users/{uid} now — kept
  // as a named constant (rather than hardcoding 'users' everywhere below)
  // so the intent of each call site stays clear.
  const profileSource = 'users';
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '' });
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [selectedAddressPlace, setSelectedAddressPlace] = useState(null);
  const [addressLabel, setAddressLabel] = useState('Home');
  const [fodyes, setFodyes] = useState({ balance: 0, backingValue: 0 });
  const [kioskCode, setKioskCode] = useState(null); // { code, expiresAt }
  const [kioskCodeSecondsLeft, setKioskCodeSecondsLeft] = useState(0);
  const [generatingKioskCode, setGeneratingKioskCode] = useState(false);
  const [showFodyesLedger, setShowFodyesLedger] = useState(false);
  const [fodyesLedger, setFodyesLedger] = useState([]);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (authLoading) return; // wait for auth state to hydrate
    if (!user) {
      navigate('/');
      return;
    }
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      // Every account (consumer or business) lives at users/{uid} now.
      // AuthContext's loadUserData already ran a one-time migration for
      // any legacy consumers/{uid} account before `authLoading` cleared
      // (this effect only runs once authLoading is false), so by the time
      // we get here users/{uid} already has the merged, up-to-date data.
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        setProfile({ ...data, _source: 'users' });
        setFormData({
          name: data.name || user.displayName || '',
          phone: data.phone || '',
        });
      }
      // Load addresses
      await loadAddresses();

      // Load Fodyes balance from user profile
      await loadUserFodyes(user.uid, 'users');
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUserFodyes = async (uid, collectionName) => {
    try {
      const userDoc = await getDoc(doc(db, collectionName, uid));
      if (userDoc.exists()) {
        const d = userDoc.data();
        setFodyes({
          balance: d.fodyesBalance || 0,
          backingValue: d.fodyesBackingValue || 0,
        });
      }
    } catch (e) {
      console.warn('Error loading user Fodyes:', e);
      setFodyes({ balance: 0, backingValue: 0 });
    }
  };

  const loadFodyesLedger = async (uid) => {
    setLedgerLoading(true);
    try {
      const ledgerRef = collection(db, 'users', uid, 'fodycoins_ledger');
      const snap = await getDocs(ledgerRef);
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.index || 0) - (a.index || 0));
      setFodyesLedger(rows);
    } catch (e) {
      console.warn('Error loading Fodyes ledger:', e);
      setFodyesLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  const openFodyesLedger = () => {
    if (user?.uid) loadFodyesLedger(user.uid);
    setShowFodyesLedger(true);
  };

  // "McDonald's-style" quick sign-in at a kiosk: generates a short-lived,
  // single-use 6-digit code (see generateKioskSignInCode in functions/
  // index.js) instead of asking the customer to type a username/password
  // on a public touchscreen. The kiosk redeems it (redeemKioskSignInCode
  // in functions/kioskLoyalty.js) to recognize the account, greet them by
  // name, and let them spend their Fodyes balance at checkout.
  const handleGenerateKioskCode = async () => {
    setGeneratingKioskCode(true);
    try {
      const generate = httpsCallable(functions, 'generateKioskSignInCode');
      const { data } = await generate();
      setKioskCode({ code: data.code, expiresAt: data.expiresAt });
    } catch (error) {
      console.error('Error generating kiosk code:', error);
      Swal.fire('Error', 'Could not generate a code right now. Please try again.', 'error');
    } finally {
      setGeneratingKioskCode(false);
    }
  };

  useEffect(() => {
    if (!kioskCode) return;
    const tick = () => {
      const secondsLeft = Math.max(0, Math.round((kioskCode.expiresAt - Date.now()) / 1000));
      setKioskCodeSecondsLeft(secondsLeft);
      if (secondsLeft <= 0) setKioskCode(null);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [kioskCode]);

  const loadAddresses = async () => {
    try {
      const snap = await getDocs(collection(db, profileSource, user.uid, 'addresses'));
      const list = [];
      snap.docs.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setAddresses(list);
    } catch (error) {
      console.error('Error loading addresses:', error);
    }
  };

  const searchAddress = async (input) => {
    if (!input || input.length < 3 || !MAPBOX_TOKEN) return;
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(input)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=5&types=address,place`
      );
      const data = await response.json();
      setAddressSuggestions(data.features || []);
    } catch (error) {
      console.error('Mapbox search error:', error);
    }
  };

  const handleAddressInput = (e) => {
    const value = e.target.value;
    setAddressQuery(value);
    setSelectedAddressPlace(null);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => searchAddress(value), 400);
  };

  const selectAddressPlace = (place) => {
    setAddressQuery(place.place_name);
    setSelectedAddressPlace(place);
    setAddressSuggestions([]);
  };

  const handleAddAddress = async () => {
    if (!selectedAddressPlace) {
      Swal.fire('Error', 'Please select an address from suggestions.', 'error');
      return;
    }
    const [lng, lat] = selectedAddressPlace.center || [0, 0];
    const context = selectedAddressPlace.context || [];
    let city = '';
    let state = '';
    let zip = '';
    context.forEach((item) => {
      if (item.id.startsWith('place.')) city = item.text;
      if (item.id.startsWith('region.')) state = item.text;
      if (item.id.startsWith('postcode.')) zip = item.text;
    });

    // If this is the first address, make it default
    const isDefault = addresses.length === 0;

    const addressData = {
      address: selectedAddressPlace.place_name,
      city,
      state,
      zip,
      lat,
      lng,
      label: addressLabel,
      isDefault,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    try {
      const ref = doc(collection(db, profileSource, user.uid, 'addresses'));
      await setDoc(ref, addressData);
      Swal.fire('Saved!', 'Address has been added.', 'success');
      setShowAddressForm(false);
      setAddressQuery('');
      setSelectedAddressPlace(null);
      setAddressLabel('Home');
      await loadAddresses();
      await reloadAddresses();
    } catch (error) {
      console.error('Error adding address:', error);
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleSetDefault = async (addressId) => {
    try {
      // Unset all others
      for (const addr of addresses) {
        if (addr.isDefault && addr.id !== addressId) {
          await updateDoc(doc(db, profileSource, user.uid, 'addresses', addr.id), {
            isDefault: false,
            updatedAt: serverTimestamp(),
          });
        }
      }
      // Set new default
      await updateDoc(doc(db, profileSource, user.uid, 'addresses', addressId), {
        isDefault: true,
        updatedAt: serverTimestamp(),
      });
      await loadAddresses();
      await reloadAddresses();
      Swal.fire('Updated!', 'Default address changed.', 'success');
    } catch (error) {
      console.error('Error setting default:', error);
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    const result = await Swal.fire({
      title: 'Delete Address?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await deleteDoc(doc(db, profileSource, user.uid, 'addresses', addressId));
      await loadAddresses();
      await reloadAddresses();
      Swal.fire('Deleted!', 'Address removed.', 'success');
    } catch (error) {
      console.error('Error deleting address:', error);
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleSaveProfile = async () => {
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        updatedAt: serverTimestamp(),
      });
      if (auth.currentUser && formData.name.trim()) {
        await updateProfile(auth.currentUser, { displayName: formData.name.trim() });
      }
      setProfile({ ...profile, name: formData.name.trim(), phone: formData.phone.trim() });
      setEditing(false);
      Swal.fire('Saved!', 'Your profile has been updated.', 'success');
    } catch (error) {
      console.error('Error saving profile:', error);
      Swal.fire('Error', error.message, 'error');
    }
  };

  const handleRemoveFavorite = async (storeId) => {
    const result = await Swal.fire({
      title: 'Remove from favorites?',
      text: 'This store will be removed from your favorites list.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Remove',
      cancelButtonText: 'Keep',
      confirmButtonColor: '#dc3545',
    });
    if (!result.isConfirmed) return;
    try {
      await addRemoveFavorite(storeId, {});
      await reloadFavorites();
      Swal.fire('Removed', 'Store removed from favorites.', 'success');
    } catch (e) {
      console.error('Error removing favorite:', e);
      Swal.fire('Error', 'Could not remove favorite.', 'error');
    }
  };

  const navigateToStore = (fav) => {
    const slug = (fav.storeName || 'store')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 60);
    navigate(`/store/${slug}-${fav.storeId}`);
  };

  if (loading) {
    return (
      <div className="ff-account-page">
        <Header />
        <div className="ff-loading-stores">
          <div className="ff-spinner"></div>
          <p>Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ff-account-page">
      <Header />
      <div className="container ff-account-content">
        {/* Profile Card */}
        <div className="ff-account-card">
          <div className="ff-account-header">
            <div className="ff-account-avatar">
              <i className="fas fa-user-circle fa-3x"></i>
            </div>
            <div className="ff-account-info">
              <h2>{profile?.name || user?.displayName || 'My Account'}</h2>
              <p className="text-muted">{user?.email}</p>
            </div>
            <button className="ff-btn ff-btn-outline" onClick={() => setEditing(!editing)}>
              {editing ? 'Cancel' : <><i className="fas fa-pen mr-1"></i> Edit</>}
            </button>
          </div>

          <div className="ff-account-body">
            {editing ? (
              <>
                <div className="ff-auth-field">
                  <label>Full Name</label>
                  <div className="ff-input-wrap">
                    <i className="fas fa-user ff-input-icon"></i>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  </div>
                </div>
                <div className="ff-auth-field">
                  <label>Phone</label>
                  <div className="ff-input-wrap">
                    <i className="fas fa-phone ff-input-icon"></i>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  </div>
                </div>
                <button className="ff-btn ff-btn-primary" onClick={handleSaveProfile}>
                  <i className="fas fa-save mr-2"></i>Save Changes
                </button>
              </>
            ) : (
              <div className="ff-account-details">
                <div className="ff-account-row">
                  <span className="ff-account-label"><i className="fas fa-envelope mr-2"></i>Email</span>
                  <span className="ff-account-value">{user?.email}</span>
                </div>
                <div className="ff-account-row">
                  <span className="ff-account-label"><i className="fas fa-user mr-2"></i>Name</span>
                  <span className="ff-account-value">{profile?.name || user?.displayName || 'Not set'}</span>
                </div>
                <div className="ff-account-row">
                  <span className="ff-account-label"><i className="fas fa-phone mr-2"></i>Phone</span>
                  <span className="ff-account-value">{profile?.phone || 'Not set'}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fodyes Card */}
        <div className="ff-account-card mt-4">
          <div className="ff-account-header" style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
            <div className="ff-account-info">
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                <i className="fas fa-coins mr-2 text-warning"></i>My Fodyes
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                {fodyes.backingValue > 0
                  ? `Your ${fodyes.balance.toLocaleString()} Fodyes are worth $${fodyes.backingValue.toFixed(2)}`
                  : 'Earn Fodyes with every order and redeem them for discounts'}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="font-weight-bold" style={{ fontSize: '1.5rem', color: '#ff5722' }}>
                {fodyes.balance.toLocaleString()}
              </div>
              <button
                className="btn btn-sm btn-outline-secondary mt-1"
                style={{ fontSize: '0.75rem', borderRadius: 12 }}
                onClick={openFodyesLedger}
              >
                <i className="fas fa-history mr-1"></i>View History
              </button>
            </div>
          </div>

          {/* Kiosk Quick Sign-In */}
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f0f0f0' }}>
            <p className="mb-2" style={{ fontSize: '0.85rem' }}>
              <i className="fas fa-store mr-2 text-secondary"></i>
              Ordering at a FodyFee kiosk? Generate a one-time code instead of typing your login there.
            </p>
            {kioskCode ? (
              <div className="text-center py-2" style={{ background: '#fff3ee', borderRadius: 12 }}>
                <div className="font-weight-bold" style={{ fontSize: '2rem', letterSpacing: 4, color: '#ff5722' }}>
                  {kioskCode.code}
                </div>
                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                  Enter this at the kiosk within {kioskCodeSecondsLeft}s
                </div>
              </div>
            ) : (
              <button
                className="btn btn-sm btn-outline-primary"
                style={{ borderRadius: 12 }}
                onClick={handleGenerateKioskCode}
                disabled={generatingKioskCode}
              >
                {generatingKioskCode ? (
                  <span className="spinner-border spinner-border-sm mr-1"></span>
                ) : (
                  <i className="fas fa-qrcode mr-1"></i>
                )}
                Generate Kiosk Code
              </button>
            )}
          </div>
        </div>

        {/* My Favorite Stores */}
        <div className="ff-account-card mt-4">
          <div className="ff-account-header" style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
            <div className="ff-account-info">
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                <i className="fas fa-heart mr-2 text-danger"></i>My Favorite Stores
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Quick access to your saved places</p>
            </div>
            <div className="font-weight-bold" style={{ fontSize: '1.1rem', color: '#ff5722' }}>
              {favorites.length}
            </div>
          </div>
          {favorites.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <i className="far fa-heart fa-2x mb-2" style={{ opacity: 0.3 }}></i>
              <p className="mb-0">No favorite stores yet. Browse and tap the heart to save!</p>
            </div>
          ) : (
            <div className="ff-favorites-list" style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
              {favorites.map((fav) => (
                <div
                  key={fav.storeId}
                  className="ff-favorite-item"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    padding: 12,
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    background: '#fafafa',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => navigateToStore(fav)}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#fff5f2'; e.currentTarget.style.borderColor = '#ff5722'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#eee'; }}
                >
                  {fav.logoUrl ? (
                    <img src={fav.logoUrl} alt={fav.storeName} style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 10, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#999' }}>
                      <i className="fas fa-store"></i>
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {fav.storeName}
                    </div>
                    {fav.address && (
                      <div style={{ fontSize: '0.8rem', color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2 }}>
                        <i className="fas fa-map-marker-alt mr-1"></i>{fav.address}
                      </div>
                    )}
                  </div>
                  <button
                    className="ff-btn-sm ff-btn-sm-danger"
                    style={{ flexShrink: 0 }}
                    onClick={(e) => { e.stopPropagation(); handleRemoveFavorite(fav.storeId); }}
                    title="Remove from favorites"
                  >
                    <i className="fas fa-heart-broken"></i>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Methods */}
        {user && profileSource && (
          <PaymentMethods userId={user.uid} userCollection={profileSource} />
        )}

        {/* Addresses Card */}
        <div className="ff-account-card mt-4">
          <div className="ff-account-header" style={{ borderBottom: '1px solid #eee', paddingBottom: 16 }}>
            <div className="ff-account-info">
              <h3 style={{ margin: 0, fontSize: '1.25rem' }}>
                <i className="fas fa-map-marker-alt mr-2 text-orange"></i>My Addresses
              </h3>
              <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>Manage delivery locations</p>
            </div>
            <button className="ff-btn ff-btn-primary" onClick={() => setShowAddressForm(!showAddressForm)}>
              {showAddressForm ? 'Cancel' : <><i className="fas fa-plus mr-1"></i>Add</>}
            </button>
          </div>

          {/* Add Address Form */}
          {showAddressForm && (
            <div className="ff-auth-body mt-3">
              <div className="ff-auth-field" style={{ position: 'relative' }}>
                <label>Search Address <span className="ff-required">*</span></label>
                <div className="ff-input-wrap">
                  <i className="fas fa-search ff-input-icon"></i>
                  <input
                    type="text"
                    value={addressQuery}
                    onChange={handleAddressInput}
                    placeholder="Start typing your address..."
                    autoFocus
                  />
                </div>
                {addressSuggestions.length > 0 && (
                  <div className="ff-suggestions" style={{ position: 'absolute', left: 0, right: 0, zIndex: 9999 }}>
                    {addressSuggestions.map((place, i) => (
                      <div key={i} className="ff-suggestion-item" onClick={() => selectAddressPlace(place)}>
                        <i className="fas fa-map-marker-alt"></i>
                        {place.place_name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ff-auth-field mt-2">
                <label>Label</label>
                <div className="d-flex gap-2">
                  {['Home', 'Work', 'Other'].map((l) => (
                    <button
                      key={l}
                      type="button"
                      className="flex-fill py-2"
                      style={{
                        border: addressLabel === l ? '2px solid #ff5722' : '2px solid #e0e0e0',
                        background: addressLabel === l ? '#fff3e0' : '#fff',
                        color: addressLabel === l ? '#ff5722' : '#666',
                        borderRadius: 10,
                        fontSize: '0.9rem',
                        cursor: 'pointer',
                        fontWeight: addressLabel === l ? 700 : 400,
                      }}
                      onClick={() => setAddressLabel(l)}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <button
                className="ff-btn ff-btn-primary ff-btn-block mt-3"
                onClick={handleAddAddress}
                disabled={!selectedAddressPlace}
              >
                <i className="fas fa-save mr-2"></i>Save Address
              </button>
            </div>
          )}

          {/* Address List */}
          <div className="ff-address-list mt-3">
            {addresses.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="fas fa-home fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                <p className="mb-0">No addresses saved yet.</p>
              </div>
            ) : (
              addresses.map((addr) => (
                <div key={addr.id} className={`ff-address-item ${addr.isDefault ? 'default' : ''}`}>
                  <div className="ff-address-info">
                    <div className="ff-address-label">
                      <span className="ff-address-badge">{addr.label || 'Address'}</span>
                      {addr.isDefault && <span className="ff-address-default-badge">Default</span>}
                    </div>
                    <p className="ff-address-text">{addr.address}</p>
                    {(addr.city || addr.state) && (
                      <p className="ff-address-city">{[addr.city, addr.state].filter(Boolean).join(', ')} {addr.zip}</p>
                    )}
                  </div>
                  <div className="ff-address-actions">
                    {!addr.isDefault && (
                      <button
                        className="ff-btn-sm"
                        onClick={() => handleSetDefault(addr.id)}
                        title="Set as default"
                      >
                        <i className="fas fa-check-circle"></i>
                      </button>
                    )}
                    <button
                      className="ff-btn-sm ff-btn-sm-danger"
                      onClick={() => handleDeleteAddress(addr.id)}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="ff-account-actions mt-4">
          <button className="ff-btn ff-btn-outline-danger" onClick={handleLogout}>
            <i className="fas fa-sign-out-alt mr-2"></i>Log Out
          </button>
        </div>
      </div>

      {/* Fodyes Ledger Modal */}
      {showFodyesLedger && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1050,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }} onClick={() => setShowFodyesLedger(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '100%', maxWidth: 720,
            maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h4 style={{ margin: 0, fontWeight: 700 }}>
                <i className="fas fa-coins mr-2 text-warning"></i>Fodyes History
              </h4>
              <button className="btn btn-sm btn-light" onClick={() => setShowFodyesLedger(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div style={{ overflowY: 'auto', flex: 1, padding: '0 24px 24px' }}>
              {ledgerLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-warning" role="status"></div>
                  <p className="text-muted mt-2">Loading transactions...</p>
                </div>
              ) : fodyesLedger.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-receipt fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                  <p>No Fodyes transactions yet.</p>
                </div>
              ) : (
                <table className="table table-sm mt-3" style={{ fontSize: '0.85rem' }}>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Description</th>
                      <th className="text-right">Coins</th>
                      <th className="text-right">Backing</th>
                      <th className="text-right">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fodyesLedger.map((tx) => {
                      const date = tx.createdAt ? new Date(tx.createdAt).toLocaleString() : '—';
                      const isEarn = tx.type === 'earn' || tx.type === 'transfer_in';
                      const isSpend = tx.type === 'spend' || tx.type === 'transfer_out';
                      return (
                        <tr key={tx.id}>
                          <td style={{ whiteSpace: 'nowrap' }}>{date}</td>
                          <td>
                            <span className={`badge badge-${isEarn ? 'success' : isSpend ? 'danger' : 'secondary'}`}
                              style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                              {tx.type?.replace('_', ' ')}
                            </span>
                          </td>
                          <td>
                            <div>{tx.description || '—'}</div>
                            {tx.data?.orderId && (
                              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                                Order #{tx.data.confirmationNumber || tx.data.orderId?.slice(-6)}
                              </div>
                            )}
                          </td>
                          <td className="text-right" style={{ fontWeight: 600, color: isEarn ? '#28a745' : isSpend ? '#dc3545' : '#333' }}>
                            {isEarn ? '+' : isSpend ? '-' : ''}{tx.amount}
                          </td>
                          <td className="text-right text-muted">
                            ${typeof tx.backingValue === 'number' ? tx.backingValue.toFixed(2) : '—'}
                          </td>
                          <td className="text-right font-weight-bold">
                            {tx.balance}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {!ledgerLoading && fodyesLedger.length > 0 && (
                <div className="mt-3 text-muted" style={{ fontSize: '0.8rem' }}>
                  <i className="fas fa-info-circle mr-1"></i>
                  Each Fodye is backed by real value pooled across all restaurants you have visited.
                  The discount you receive is always equal to or less than the total backing value.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AccountPage;
